import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CENTER,
  defaultButtonStyles,
  defaultGapping,
  defaultLayouts,
  defaultTypography,
  golfstatusColors,
  golfstatusTypography,
  GSActionBar,
  GSButton,
  GSDivider,
  GSSidePanelPage, START,
  useMediaQuery,
} from "golfstatus_react_components";
import {
  faBolt,
  faChevronLeft,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";

// import scss
import "./tournament-round-team-hole-assignments.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentTeams,
  selectLoading,
  selectQualifiedTeams,
  setCurrentTeam,
} from "../../../../reducers/teamsSlice";
import {
  clearRoundNotifications,
  getLinkedRoundHoleAssignments,
  getRound,
  getRoundHoleAssignmentList,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLinkedRoundHoleAssignments,
  selectRoundNotifications,
  setCurrentHoleAssignment,
  selectLoading as selectRoundLoading,
} from "../../../../reducers/roundSlice";
import { useRoundHooks, getRoundName, getSubRounds, getRoundGroups } from "../../../../hooks/round/roundHooks";
import {
  filterUnassignedTeams,
  useHoleAssignmentBanner,
} from "../../../../hooks/round/roundHoleAssignmentHooks";
import {
  useNotificationAction,
  useNotificationBanner,
} from "../../../../hooks/notificationHooks";
import { getTournamentFlights } from "../../../../reducers/flightSlice";
import TournamentRoundHoleAssignmentList from "./tournament-round-hole-assignment-list";
import TournamentRoundHoleAssignmentTeamList from "./tournament-round-hole-assignment-team-list";
import TournamentRoundChangeRound from "../tournament-round-change-round";

// name the component
const TournamentRoundTeamHoleAssignments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch("/:tournamentID/rounds/:roundID/hole-assignments");
  const { isPhone } = useMediaQuery();

  // local state
  //whether the rounds this page can switch to are showing under the header
  const [changingRound, setChangingRound] = useState(false);

  // selectors
  const currentRound = useSelector(selectCurrentRound);
  const currentRoundHoleAssignments = useSelector(
    selectCurrentRoundHoleAssignments,
  );
  const tournamentTeams = useSelector(selectQualifiedTeams);
  const linkedRoundHoleAssignments = useSelector(
    selectLinkedRoundHoleAssignments,
  );
  const notifications = useSelector(selectRoundNotifications);
  const teamLoading = useSelector(selectLoading);
  const roundLoading = useSelector(selectRoundLoading);
  //moving a team shows a spinner on the cards involved, and adding a tee time
  //spins on its own button, so neither takes the whole page over with it
  const assignmentLoading = [
    "saveRoundHoleAssignment",
    "swapRoundHoleAssignments",
    "addAdditionalHoleAssignment",
  ];
  const loading = [...teamLoading, ...roundLoading].filter?.(
    (l) => !assignmentLoading.includes(l),
  );


  // hooks
  const { allRounds } = useRoundHooks();

  //rounds that share this round's number are its sub rounds. with them, or with
  //other round numbers in the tournament, there is a round to change to.
  const linkedRounds = getSubRounds(currentRound, allRounds);
  const hasSubRounds = linkedRounds?.length > 1;
  const canChangeRound =
    hasSubRounds || getRoundGroups(allRounds)?.length > 1;

  const resetSelections = useCallback(() => {
    dispatch(setCurrentHoleAssignment({}));
    dispatch(setCurrentTeam({}));
  }, [dispatch]);

  useNotificationAction(
    "saved",
    notifications,
    resetSelections,
    "Hole Assignment",
  );

  // effects
  useEffect(() => {
    dispatch(getRound(match.params.roundID));
    dispatch(getRoundHoleAssignmentList(match.params.roundID));
  }, [dispatch, match?.params?.roundID]);

  //the round in the store trails the route while a new one loads, so its linked
  //set describes the round being left rather than the one being opened
  const roundLoaded = currentRound?.id === match?.params?.roundID;

  //a stable key, so the fetch doesn't re-run on every render of the derived list
  const linkedRoundIDs = roundLoaded
    ? linkedRounds
        ?.filter?.((r) => r?.id !== match?.params?.roundID)
        ?.map?.((r) => r?.id)
        ?.join?.(",")
    : "";

  //the linked rounds are only asked for once the round is in, and until they are
  //the unassigned list can't tell which teams they hold. the page stays loading
  //until they've been asked for this round, then while the request is out.
  const linkedRequestKey = `${match?.params?.roundID}:${linkedRoundIDs}`;
  const [linkedRequestedFor, setLinkedRequestedFor] = useState(null);

  useEffect(() => {
    if (!roundLoaded) {
      return;
    }
    dispatch(
      getLinkedRoundHoleAssignments({
        roundID: match?.params?.roundID,
        roundIDs: linkedRoundIDs ? linkedRoundIDs.split(",") : [],
      }),
    );
    setLinkedRequestedFor(`${match?.params?.roundID}:${linkedRoundIDs}`);
  }, [dispatch, roundLoaded, linkedRoundIDs, match?.params?.roundID]);

  const pageLoading =
    loading?.length > 0 || linkedRequestedFor !== linkedRequestKey;

  useEffect(() => {
    dispatch(getTournamentTeams(match.params.tournamentID));
    dispatch(getTournamentFlights(match.params.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  useEffect(() => {
    // clear any selections carried in from another page - the team may no
    // longer be selectable here (disqualified, already assigned, etc.)
    resetSelections();
    return () => {
      resetSelections();
      dispatch(clearRoundNotifications());
    };
  }, [dispatch, resetSelections]);

  const leftNavigation = () => {
    dispatch(clearRoundNotifications());
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Round ${getRoundName(currentRound)} Hole Assignments`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const defaultBanner = useHoleAssignmentBanner(
    currentRound,
    match?.params?.roundID,
  );

  const getHeader = () => {
    const title = `Round ${getRoundName(currentRound)} Hole Assignments`;

    //with a single round there is nothing to change to, so the header is just
    //the title.
    if (!canChangeRound) {
      return title;
    }

    return (
      <div
        style={{
          ...defaultLayouts.vStack,
          ...defaultLayouts.fullWidth,
          ...defaultGapping.smallGap,
        }}
      >
        <div>{title}</div>
        <div
          style={{
            ...defaultLayouts.hStack.align(START, CENTER),
            ...defaultGapping.mediumGap,
          }}
        >
          {/* neither a round's own label nor the sub round in the title says
              what the round is numbered, so the number sits here */}
          {(hasSubRounds || currentRound?.roundName) && (
            <>
              <div style={{ ...defaultTypography.bodyRegular }}>
                {`Round ${currentRound?.number}`}
              </div>
              <GSDivider
                style={{
                  width: "1px",
                  height: "16px",
                  backgroundColor: golfstatusColors.grey300,
                }}
              />
            </>
          )}
          <GSButton
            //the button title carries letter spacing and a tall line height of
            //its own, neither of which the label beside it has
            {...defaultButtonStyles.text.withStyle({
              fontSize: golfstatusTypography.smallFont,
              fontWeight: golfstatusTypography.regularFontWeight,
              letterSpacing: "normal",
              lineHeight: "normal",
              whiteSpace: "nowrap",
              color: golfstatusColors.cyan700,
            })}
            type="transparent"
            title={changingRound ? "Close" : "Change Round"}
            onClick={() => setChangingRound(!changingRound)}
          />
        </div>
      </div>
    );
  };

  const getPageActions = () => {
    return [
      {
        title: "Auto Assign",
        actionIcon: faBolt,
        type: "black no-wrap mobile-icon",
        actionClick: () => navigate("auto-assign"),
      },
      {
        type: "no-wrap light-grey mobile-icon",
        actionIcon: faGear,
        actionClick: () => navigate("settings"),
      },
    ];
  };

  const hasTournamentTeams = () => {
    return tournamentTeams?.length > 0;
  };

  const hasUnassignedTeams = () => {
    //a team assigned in a linked round is spoken for here too
    const assignments = [
      ...currentRoundHoleAssignments,
      ...linkedRoundHoleAssignments,
    ];

    return filterUnassignedTeams(tournamentTeams, assignments).length > 0;
  };

  const divRef = useRef();

  const getContent = () => {
    let styles = {
      ...defaultLayouts.fullWidth,
    };
    if (tournamentTeams?.length > 0) {
      isPhone()
        ? (styles = {
            ...styles,
            ...defaultLayouts.vStack,
          })
        : (styles = {
            ...styles,
            ...defaultLayouts.hStack,
            ...defaultLayouts.fullHeight,
            flex: "1 1 auto",
            minHeight: 0,
          });
    }
    return (
      <gs-form style={isPhone() ? {} : { overflow: "hidden" }}>
        <GSActionBar
          type="form-header"
          style={
            isPhone()
              ? { background: golfstatusColors.white, opacity: 1.0, zIndex: 1 }
              : { flex: "0 0 auto" }
          }
          header={getHeader()}
          pageActions={getPageActions()}
        ></GSActionBar>
        <TournamentRoundChangeRound
          currentRound={currentRound}
          rounds={allRounds}
          open={changingRound}
          onClose={() => setChangingRound(false)}
        />
        <div ref={divRef} style={styles}>
          <TournamentRoundHoleAssignmentList />
          {hasTournamentTeams() && hasUnassignedTeams() && (
            <TournamentRoundHoleAssignmentTeamList />
          )}
        </div>
      </gs-form>
    );
  };

  const timeoutAction = () => {
    dispatch(clearRoundNotifications());
  };

  //the banner already says every action saves as it happens, and the cards carry
  //their own spinner, so neither half of a save needs to take it over. anything
  //that goes wrong still comes through.
  const bannerNotifications = notifications?.filter?.(
    (n) => !["pending", "saved"].includes(n?.result),
  );

  const [banner] = useNotificationBanner({
    notifications: bannerNotifications,
    timeoutAction,
    defaultBanner,
  });

  return (
    // name the component tag
    <tournament-round-team-hole-assignments>
      <GSSidePanelPage
        loading={pageLoading}
        header={getNavigation()}
        banner={banner}
        content={getContent()}
      ></GSSidePanelPage>
    </tournament-round-team-hole-assignments>
  );
};

// export the new name
export default TournamentRoundTeamHoleAssignments;
